import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { SiteShiftConfigApiDto } from 'src/app/shared/models/drilling.models';
import { ShiftConfigService } from 'src/app/services/shift-config.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

const DEFAULT_CONFIG: SiteShiftConfigApiDto = {
  siteId: 0,
  shiftsPerDay: 3,
  shiftLabels: ['Morning', 'Day', 'Night'],
  firstShiftStartHour: 6,
};

@Component({
  selector: 'app-shift-config',
  templateUrl: './shift-config.component.html',
  styleUrls: ['./shift-config.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, DecimalPipe],
})
export class ShiftConfigComponent {
  private readonly shiftConfigService = inject(ShiftConfigService);
  protected readonly utilService = inject(UtilisateurService);
  private readonly fonctionsService = inject(FonctionsService);

  protected formData: SiteShiftConfigApiDto = { ...DEFAULT_CONFIG };
  protected readonly isLoaded = signal(false);
  protected readonly showError = signal(false);

  protected readonly shiftsOptions = [2, 3];

  constructor() {
    effect(() => {
      const site = this.utilService.currentSite();
      if (site?.siteId) {
        this.loadConfig(site.siteId);
      } else {
        this.formData = { ...DEFAULT_CONFIG };
        this.isLoaded.set(true);
      }
    });
  }

  protected get shiftIndices(): number[] {
    return Array.from({ length: this.formData.shiftsPerDay }, (_, i) => i);
  }

  protected onShiftsPerDayChange(value: number): void {
    const current = this.formData.shiftLabels;
    const defaultLabels = DEFAULT_CONFIG.shiftLabels;
    if (value > current.length) {
      this.formData.shiftLabels = [
        ...current,
        ...defaultLabels.slice(current.length, value),
      ];
    } else {
      this.formData.shiftLabels = current.slice(0, value);
    }
    this.formData.shiftsPerDay = value;
  }

  protected computedStartHour(index: number): number {
    const duration = 24 / this.formData.shiftsPerDay;
    return (this.formData.firstShiftStartHour + index * duration) % 24;
  }

  private loadConfig(siteId: number): void {
    this.isLoaded.set(false);
    this.shiftConfigService.getShiftConfig(siteId).subscribe({
      next: result => {
        this.formData = { ...result };
        this.isLoaded.set(true);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.formData = { ...DEFAULT_CONFIG, siteId };
        } else {
          this.fonctionsService.launch_toast(false);
        }
        this.isLoaded.set(true);
      },
    });
  }

  protected save(): void {
    const { shiftsPerDay, shiftLabels, firstShiftStartHour } = this.formData;

    if (shiftsPerDay !== 2 && shiftsPerDay !== 3) {
      this.showError.set(true);
      return;
    }
    if (shiftLabels.some(l => !l?.trim())) {
      this.showError.set(true);
      return;
    }
    if (firstShiftStartHour < 0 || firstShiftStartHour > 23) {
      this.showError.set(true);
      return;
    }

    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.fonctionsService.loading_screen(true);
    this.shiftConfigService.putShiftConfig(siteId, { ...this.formData, siteId }).subscribe({
      next: result => {
        this.formData = { ...result };
        this.showError.set(false);
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }
}
